import { Action, createReducer, on } from '@ngrx/store';
import { v4 as uuid } from 'uuid';

import { DocumentActions } from './document.actions';
import { DocumentState } from './document.state';
import { MimeTypeDocumentTypeMapper } from '../../features/document/constants/document-type.mappers';
import { FILE_UPLOAD_LIMIT } from '../../features/document/constants/document-upload-limit';
import { TemporaryFile, TemporaryFileSaveStatus } from '../../features/document/models/temporary-file.model';
import { FileUploadResult } from '../../features/shared/services/file/file.service';
import { measureReducer } from '../../features/shared/utils/runtime-reducer-stats';

export const initialState: DocumentState = {
  loading: false,
  submitting: false,
};

const featureReducer = measureReducer(
  createReducer(
    initialState,
    on(
      DocumentActions.Reset,
      (state, action): DocumentState => ({
        ...initialState,
      })
    ),
    on(
      DocumentActions.FilesAdded,
      (state, action): DocumentState => ({
        ...state,
        files: [
          ...(state.files || []),
          ...action.files.map((file) => {
            const id = uuid();
            return {
              file,
              uploadStatus: FileUploadResult.NOT_STARTED,
              uuid: id,
              libraryShortName: action.libraryShortName,
              assetShortName: action.assetShortName,
              originalFilename: {
                [MimeTypeDocumentTypeMapper[file.type]]: file.name,
              },
              savedStatus: TemporaryFileSaveStatus.NOT_SAVED,
            } as TemporaryFile;
          }),
        ].map((f, i) => ({
          ...f,
          uploadStatus: i + 1 > FILE_UPLOAD_LIMIT ? FileUploadResult.SKIPPED : f.uploadStatus,
        })),
      })
    ),
    on(
      DocumentActions.FileFromUrlAdded,
      (state, action): DocumentState => ({
        ...state,
        files: [
          ...(state.files || []),
          {
            fileFromUrl: action.fileFromUrl,
            uploadStatus: FileUploadResult.NOT_STARTED,
            uuid: uuid(),
            libraryShortName: action.libraryShortName,
            assetShortName: action.assetShortName,
            originalFilename: undefined,
            savedStatus: TemporaryFileSaveStatus.NOT_SAVED,
          } as TemporaryFile,
        ].map((f, i) => ({
          ...f,
          uploadStatus: i + 1 > FILE_UPLOAD_LIMIT ? FileUploadResult.SKIPPED : f.uploadStatus,
        })),
      })
    ),
    on(
      DocumentActions.LoadFile,
      (state, action): DocumentState => ({
        ...state,
        files: (state.files || []).map((file) =>
          file.uuid === action.file.uuid
            ? ({
                ...file,
                uploadStatus: FileUploadResult.IN_PROGRESS,
              } as TemporaryFile)
            : file
        ),
      })
    ),
    on(
      DocumentActions.LoadFileSuccess,
      (state, action): DocumentState => ({
        ...state,
        // action.file is a snapshot taken when the upload started; the flash extraction
        // toggle may have changed since, so the store's current value wins.
        files: state.files.map((file) =>
          file.uuid === action.file.uuid
            ? { ...action.file, flashExtractionRequested: file.flashExtractionRequested }
            : file
        ),
      })
    ),
    on(
      DocumentActions.DeleteFile,
      (state, action): DocumentState => ({
        ...state,
        files: state.files.filter((file) => file.uuid !== action.file.uuid),
      })
    ),
    on(
      DocumentActions.SetFlashExtraction,
      (state, action): DocumentState => ({
        ...state,
        files: (state.files || []).map((file) =>
          file.uuid === action.file.uuid
            ? { ...file, flashExtractionRequested: action.flashExtractionRequested }
            : file
        ),
      })
    ),
    on(
      DocumentActions.ClearFlashExtraction,
      (state): DocumentState => ({
        ...state,
        files: (state.files || []).map((file) =>
          file.flashExtractionRequested ? { ...file, flashExtractionRequested: false } : file
        ),
      })
    ),
    on(
      DocumentActions.LoadFlashExtractionStatsSuccess,
      (state, action): DocumentState => ({
        ...state,
        flashExtractionStats: action.stats,
      })
    ),
    on(
      DocumentActions.SubmitFiles,
      (state, action): DocumentState => ({
        ...state,
        submitting: true,
      })
    ),
    on(
      DocumentActions.SubmitFilesFail,
      DocumentActions.SubmitFilesSuccess,
      (state, action): DocumentState => ({
        ...state,
        submitting: false,
      })
    ),

    on(DocumentActions.Sort, (state, action): DocumentState => ({ ...state, sort: action.sort }))
  )
);

export function reducer(state: DocumentState | undefined, action: Action) {
  return featureReducer(state, action);
}
